import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  VentilationModeCommands,
  VentilationModeProperties,
  VentilationOperatingModesActivePrograms,
  VentilationPermanentCommands,
  VentilationPermanentProperties,
  VentilationProgramProperties,
} from '@vi-iot/client';

import { InstallationFeaturesStore } from '@store';

import { ViConfirmObject } from '@vi-iot/ui/index';
import { CustomTranslationsService } from '../../../../operational-data-common/translations';
import { GTMService } from '../../../../shared/services';

@Component({
  selector: 'app-ventilation-mode-panel',
  templateUrl: './ventilation-mode-panel.component.html',
  styleUrls: ['./ventilation-mode-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationModePanelComponent implements OnInit {
  ventilationMode$: Observable<Feature<VentilationModeProperties, VentilationModeCommands>>;
  ventilationProgram$: Observable<Feature<VentilationProgramProperties, never>>;
  ventilationPermanent$: Observable<
    Feature<VentilationPermanentProperties, VentilationPermanentCommands>
  >;

  ventilationPermanentOptions: Array<{ name: string | number; value: string }>;
  FeatureNames = FeatureNames;
  VentilationOperatingModesActivePrograms = VentilationOperatingModesActivePrograms;
  isContinuousSensorOverrideMode: boolean;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  choosingModesBlocked = false;
  isWarningAccepted: boolean;
  viConfirmObject: ViConfirmObject;

  constructor(
    public gtmService: GTMService,
    private translateService: TranslateService,
    private customTranslationsService: CustomTranslationsService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.initViConfirmObject();
    this.ventilationMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationMode
    );

    this.ventilationMode$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((feature) => {
      this.isContinuousSensorOverrideMode =
        feature?.properties?.value?.value ===
        VentilationOperatingModesActivePrograms.continuousSensorOverride;
    });

    this.ventilationProgram$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationProgram
    );

    this.ventilationPermanent$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.ventilationPermanent)
      .pipe(
        tap((permanent: Feature<VentilationPermanentProperties, VentilationPermanentCommands>) => {
          this.ventilationPermanentOptions = permanent?.commands?.setLevel.params?.level?.constraints.enum.map(
            (item) => ({
              name: this.translateService.instant(
                this.customTranslationsService.VENTILATION_PROGRAMS[item]
              ),
              value: item,
            })
          );
        })
      );
  }

  private initViConfirmObject(): void {
    this.viConfirmObject = {
      title: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.WARNING_TITLE'
      ),
      content: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.WARNING'
      ),
      cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
      confirmBtn: this.translateService.instant('GENERAL.OK'),
    };
  }

  confirmWarning(mode: string): void {
    this.onOptionChange({ value: mode });
    this.isWarningAccepted = true;
  }

  isBlockedByContinuousSensorOverride(): boolean {
    if (this.isContinuousSensorOverrideMode) {
      return !this.isWarningAccepted;
    }
  }

  onOptionChange(option: { value: string }): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.ventilationMode,
      option.value,
      'value',
      'mode',
      'setMode'
    );
  }

  onPermanentLevelChange(level: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.ventilationPermanent,
      level,
      'value',
      'level',
      'setLevel'
    );
  }

  trackVentilationOperatingMode(): void {
    this.gtmService.track('onSelect', 'Ventilation`s operating mode', 'operatingMode');
  }
}
